import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerdictTimeline = ({ timeline, onViewDiff }) => {
  const [expandedEntry, setExpandedEntry] = useState(null);

  const getVerdictColor = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'true': return 'bg-success text-success-foreground';
      case 'false': return 'bg-destructive text-destructive-foreground';
      case 'misleading': return 'bg-warning text-warning-foreground';
      case 'unverified': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'verdict_change': return 'RotateCcw';
      case 'evidence_added': return 'Plus';
      case 'confidence_updated': return 'TrendingUp';
      case 'peer_review': return 'Users';
      case 'source_added': return 'Link';
      default: return 'Clock';
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'verdict_change': return 'text-warning bg-warning/10';
      case 'evidence_added': return 'text-success bg-success/10';
      case 'confidence_updated': return 'text-primary bg-primary/10';
      case 'peer_review': return 'text-accent bg-accent/10';
      case 'source_added': return 'text-secondary bg-secondary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const toggleExpanded = (index) => {
    setExpandedEntry(expandedEntry === index ? null : index);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Verdict Timeline</h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="GitBranch"
            iconPosition="left"
          >
            View All Changes
          </Button>
          <Button
            variant="ghost"
            size="icon"
            iconName="Download"
          />
        </div>
      </div>
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

        <div className="space-y-6">
          {timeline?.map((entry, index) => (
            <div key={index} className="relative flex items-start space-x-4">
              {/* Timeline Dot */}
              <div className={`
                relative z-10 w-12 h-12 rounded-full border-4 border-card shadow-soft
                flex items-center justify-center
                ${getChangeTypeColor(entry?.type)}
              `}>
                <Icon name={getChangeTypeIcon(entry?.type)} size={18} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="bg-surface border border-border rounded-lg p-4 shadow-soft">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-foreground">{entry?.title}</h4>
                      <div className="flex items-center space-x-3 mt-1 text-sm text-muted-foreground">
                        <span>{entry?.timestamp}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="User" size={14} />
                          <span>{entry?.reviewer}</span>
                        </div>
                        {entry?.efcsnVerified && (
                          <>
                            <span>•</span>
                            <div className="flex items-center space-x-1">
                              <Icon name="Shield" size={14} className="text-primary" />
                              <span className="text-primary">EFCSN</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {entry?.verdict && (
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor(entry?.verdict)}`}>
                        {entry?.verdict}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-foreground mb-3">{entry?.description}</p>

                  {/* Changes Summary */}
                  {entry?.changes && (
                    <div className="space-y-2 mb-3">
                      {entry?.changes?.map((change, changeIndex) => (
                        <div key={changeIndex} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span className="text-sm text-foreground">{change?.field}</span>
                          <div className="flex items-center space-x-2 text-sm">
                            <span className="text-destructive line-through">{change?.from}</span>
                            <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                            <span className="text-success">{change?.to}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Confidence Score Change */}
                  {entry?.confidenceChange && (
                    <div className="flex items-center space-x-3 mb-3 p-2 bg-primary/5 rounded">
                      <Icon name="TrendingUp" size={16} className="text-primary" />
                      <span className="text-sm text-foreground">Confidence Score:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">{entry?.confidenceChange?.from}%</span>
                        <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-primary">{entry?.confidenceChange?.to}%</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <div className="flex items-center space-x-2">
                      {entry?.hasDiff && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="GitCompare"
                          iconPosition="left"
                          onClick={() => onViewDiff(entry?.id)}
                        >
                          View Diff
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={expandedEntry === index ? "ChevronUp" : "ChevronDown"}
                        iconPosition="left"
                        onClick={() => toggleExpanded(index)}
                      >
                        {expandedEntry === index ? "Less" : "More"} Details
                      </Button>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Button variant="ghost" size="icon" iconName="Copy" />
                      <Button variant="ghost" size="icon" iconName="Share" />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedEntry === index && entry?.details && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      {entry?.details?.sources && (
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">Sources Added:</h5>
                          <div className="space-y-1">
                            {entry?.details?.sources?.map((source, sourceIndex) => (
                              <div key={sourceIndex} className="flex items-center space-x-2 text-sm">
                                <Icon name="Link" size={14} className="text-muted-foreground" />
                                <span className="text-foreground truncate">{source?.title}</span>
                                <span className="text-muted-foreground">({source?.publisher})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {entry?.details?.methodology && (
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">Methodology:</h5>
                          <p className="text-sm text-muted-foreground">{entry?.details?.methodology}</p>
                        </div>
                      )}
                      
                      {entry?.details?.peerReviews && (
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">Peer Reviews:</h5>
                          <div className="space-y-2">
                            {entry?.details?.peerReviews?.map((review, reviewIndex) => (
                              <div key={reviewIndex} className="flex items-start space-x-2 p-2 bg-muted/30 rounded">
                                <Icon name="User" size={14} className="text-muted-foreground mt-0.5" />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-sm font-medium text-foreground">{review?.reviewer}</span>
                                    <span className="text-xs text-muted-foreground">{review?.timestamp}</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{review?.comment}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VerdictTimeline;